import React from 'react';
import {
  Shield,
  Zap,
  MessageSquare,
  Target,
  Lock,
  Activity,
  ChevronRight,
  CheckCircle2,
  Terminal,
  Search,
  FileText
} from 'lucide-react';
import { JaegerLogo } from './JaegerLogo';

export const LandingPage: React.FC = () => {
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.jaegerai.space';

  const features = [
    {
      icon: MessageSquare,
      title: 'Natural Language Interface',
      description: 'Execute security scans using conversational commands. No complex CLI syntax needed.'
    },
    {
      icon: Shield,
      title: '150+ Security Tools',
      description: 'Access industry-standard tools like nmap, nuclei, sqlmap, and more through HexStrike AI.'
    },
    {
      icon: Zap,
      title: 'Autonomous Execution',
      description: 'AI-powered intent parsing automatically selects and chains the right tools for your scan.'
    },
    {
      icon: Target,
      title: 'Multi-Target Management',
      description: 'Organize and track security assessments across multiple projects and targets.'
    },
    {
      icon: Activity,
      title: 'Real-Time Streaming',
      description: 'Watch scan progress in real-time with Server-Sent Events streaming.'
    },
    {
      icon: Lock,
      title: 'Enterprise Security',
      description: 'Secure authentication, audit logs, and role-based access control for teams.'
    }
  ];

  const howItWorks = [
    {
      icon: MessageSquare,
      title: 'Describe Your Goal',
      description: 'Tell Jaeger what you want to scan in plain English. "Check example.com for SQL injection vulnerabilities"'
    },
    {
      icon: Search,
      title: 'AI Intent Parsing',
      description: 'OpenAI analyzes your request and determines the best security tools and scan strategy.'
    },
    {
      icon: Terminal,
      title: 'Tool Execution',
      description: 'HexStrike MCP server executes the selected tools and chains them intelligently.'
    },
    {
      icon: FileText,
      title: 'Structured Results',
      description: 'Get actionable findings with severity ratings, remediation steps, and full scan reports.'
    }
  ];

  const stats = [
    { value: '150+', label: 'Security Tools' },
    { value: '100%', label: 'AI-Powered' },
    { value: 'SSE', label: 'Real-Time Streaming' },
    { value: '24/7', label: 'Always Available' }
  ];

  const handleGetStarted = () => {
    window.location.href = APP_URL;
  };

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-bg text-gray-100">
      {/* Navigation */}
      <nav className="border-b border-white/10 backdrop-blur-xl bg-bg/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary border border-primary/20 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                <JaegerLogo className="w-6 h-6" />
              </div>
              <span className="text-xl font-bold text-white">Jaeger AI</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href={APP_URL}
                className="text-sm text-gray-300 hover:text-white transition-colors"
              >
                Sign In
              </a>
              <button
                onClick={handleGetStarted}
                className="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg shadow-primary/20"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8">
              <Shield size={16} />
              Autonomous Cybersecurity Co-Pilot
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Security Scanning
              <br />
              <span className="bg-gradient-to-r from-primary via-blue-400 to-indigo-400 text-transparent bg-clip-text">
                Powered by AI
              </span>
            </h1>

            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Execute 150+ offensive security tools through natural language.
              No complex commands, just describe what you want to scan.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleGetStarted}
                className="group bg-primary hover:bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-200 shadow-2xl shadow-primary/30 flex items-center gap-2"
              >
                Start Scanning Free
                <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </button>
              <button
                onClick={scrollToFeatures}
                className="px-8 py-4 rounded-xl text-lg font-semibold border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all duration-200"
              >
                Learn More
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-3xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Professional-grade security testing tools accessible through a simple chat interface
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-card border border-white/10 rounded-2xl p-8 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform duration-300 border border-primary/20">
                  <feature.icon size={28} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-b from-bg to-card/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From conversation to comprehensive security assessment in four simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-card border border-white/10 rounded-2xl p-8 h-full">
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm border-4 border-bg">
                    {index + 1}
                  </div>
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6 border border-primary/20">
                    <step.icon size={28} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="text-primary/30" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Showcase */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary/10 via-card to-indigo-600/10 border border-white/10 rounded-3xl p-12 md:p-16">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="flex-1">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  150+ Security Tools
                  <br />
                  <span className="text-primary">At Your Fingertips</span>
                </h2>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  Powered by HexStrike AI MCP, Jaeger gives you access to the industry's most trusted
                  offensive security tools including nmap, nuclei, sqlmap, nikto, ffuf, and many more.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['nmap', 'nuclei', 'sqlmap', 'nikto', 'ffuf', 'gobuster', 'masscan', 'subfinder'].map((tool) => (
                    <div
                      key={tool}
                      className="px-4 py-2 bg-bg/50 border border-white/10 rounded-lg text-sm text-gray-300 font-mono"
                    >
                      {tool}
                    </div>
                  ))}
                  <div className="px-4 py-2 bg-primary/20 border border-primary/30 rounded-lg text-sm text-primary font-semibold">
                    +142 more
                  </div>
                </div>
              </div>
              <div className="flex-1 relative">
                <div className="bg-card border border-white/10 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-critical" />
                    <div className="w-3 h-3 rounded-full bg-warning" />
                    <div className="w-3 h-3 rounded-full bg-success" />
                  </div>
                  <div className="space-y-3 font-mono text-sm">
                    <div className="text-primary">$ jaeger</div>
                    <div className="text-gray-400">
                      <span className="text-gray-500">{'>'}</span> Scan example.com for vulnerabilities
                    </div>
                    <div className="text-gray-500">
                      <Terminal className="inline mr-2" size={14} />
                      Executing reconnaissance...
                    </div>
                    <div className="text-gray-500">
                      <CheckCircle2 className="inline mr-2 text-success" size={14} />
                      Found 12 subdomains
                    </div>
                    <div className="text-gray-500">
                      <Activity className="inline mr-2 text-warning animate-pulse" size={14} />
                      Scanning for vulnerabilities...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-indigo-600/20 pointer-events-none" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your
            <br />
            Security Workflow?
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Join security professionals using AI to accelerate their penetration testing and vulnerability assessments.
          </p>
          <button
            onClick={handleGetStarted}
            className="group bg-primary hover:bg-blue-600 text-white px-10 py-5 rounded-xl text-lg font-semibold transition-all duration-200 shadow-2xl shadow-primary/30 inline-flex items-center gap-3"
          >
            Get Started for Free
            <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </button>
          <p className="text-sm text-gray-500 mt-6">
            No credit card required · Start scanning in minutes
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary border border-primary/20">
                  <JaegerLogo className="w-6 h-6" />
                </div>
                <span className="text-lg font-bold text-white">Jaeger AI</span>
              </div>
              <p className="text-gray-400 text-sm max-w-sm">
                Autonomous cybersecurity co-pilot that democratizes offensive security through natural language interaction.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href={`${APP_URL}/docs`} className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href={APP_URL} className="hover:text-white transition-colors">Get Started</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href={`${APP_URL}/privacy`} className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href={`${APP_URL}/terms`} className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-500">
            © 2026 Jaeger AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
